import React from 'react'
import { path } from 'ramda'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import {
  displayPriceForSubscription,
  subStatusDisplay,
  getSubscriptionDisplayName,
  getSubscriptionTypeClasses,
  CAM_FEATURES,
  isCameraSubscription,
  isCellularDevice,
  getExpiredCellularSubscription,
  getSubscriptionConfig
} from './index'
import { getDeviceLabel, hasVideo } from '../../common/ambient'
import AutoRenewToggle from './AutoRenewToggle'
import StayOnPlanButton from './StayOnPlanButton'
import './DeviceSubscriptionCard.less'

export default function DeviceSubscriptionCard ({ macAddress, subscriptions, user, device, userActions }) {
  // Get device info for this MAC address
  const devices = path(['devices'], device) || []
  const deviceForCard = devices.find(d => d.macAddress === macAddress)

  const deviceLabel = deviceForCard ? getDeviceLabel(deviceForCard) : 'Unknown Device'
  const deviceLocation = deviceForCard?.info?.coords?.location || 'Location not set'

  // Group subscriptions by type
  const cameraSubscriptions = subscriptions.filter(sub => isCameraSubscription(sub.productName))
  const cellularSubscriptions = subscriptions.filter(sub => !isCameraSubscription(sub.productName))

  // Check if device can have a camera attached (requires lastData)
  const canHaveCamera = !!deviceForCard?.lastData

  // Check if device already has video capability
  const deviceHasVideo = hasVideo(deviceForCard)

  // Determine which promotional content to show when no camera subscription exists
  const showFreeUpgradeCard = cameraSubscriptions.length === 0 && deviceHasVideo
  const showConnectCameraPromo = cameraSubscriptions.length === 0 && !deviceHasVideo && canHaveCamera

  // Check if device is cellular but has no cellular subscription
  const showAddCellPlanPromo = cellularSubscriptions.length === 0 &&
                                deviceForCard &&
                                isCellularDevice(deviceForCard)

  // Get expired subscription info if exists
  const expiredCellularSub = showAddCellPlanPromo ? getExpiredCellularSubscription(user, macAddress) : null

  // Camera features for "connect camera" promotional card
  const cameraFeatures = [
    'Time-lapse weather videos',
    'Standard daily storage'
  ]

  const cameraUpgradeFeatures = [
    'Extended storage (up to 60 days)',
    'Video library & management',
    'Advanced video insights with weather data'
  ]

  // Upgrade features for devices with video but no subscription
  const upgradeFeatures = [
    'Camera resolution',
    'Regeneration rate',
    'Timelapse storage'
  ]

  return (
    <div className='device-subscription-card'>
      <div className='card-header'>
        {deviceLabel}
      </div>
      <div className='card-body'>
        <div className='device-meta'>
          <div className='meta-row mac-address'>
            <span className='label'>MAC:</span>
            <span className='value'>{macAddress}</span>
          </div>
          <div className='meta-row location'>
            <span className='label'>Location:</span>
            <span className='value'>{deviceLocation}</span>
          </div>
        </div>

        {showAddCellPlanPromo && (
          <div className='subscription-group'>
            <h3 className='subscription-group-heading'>Cellular Plan</h3>
            <div className='add-cellular-promo'>
              <div className='add-plan-box'>
                <div className='add-plan-content'>
                  <div className='plan-info'>
                    <h4>KestrelMet Standard 1 Year Cellular Plan</h4>
                    <div className='price'>$99.99/year</div>
                    {expiredCellularSub && (
                      <div className='expired-notice'>
                        Previous plan expired:<br/>{moment(expiredCellularSub.timestamp).format('MMM D, YYYY')}
                      </div>
                    )}
                  </div>
                  <Link
                    to={`/subscriptions/checkout?subscription=kestrelmet-one-year-subscription-new&deviceId=${deviceForCard._id}`}
                    className='btn btn-primary btn-lg'
                  >
                    Add Cellular Plan
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {cellularSubscriptions.length > 0 && (
          <div className='subscription-group'>
            <h3 className='subscription-group-heading'>Cellular Plan</h3>
            <div className='subscriptions-list'>
              {cellularSubscriptions.map((sub, idx) => (
                <SubscriptionItem
                  key={idx}
                  sub={sub}
                  user={user}
                  device={device}
                  userActions={userActions}
                  index={subscriptions.indexOf(sub)}
                />
              ))}
            </div>
          </div>
        )}

        {showFreeUpgradeCard && (
          <div className='subscription-group'>
            <h3 className='subscription-group-heading'>Camera Subscription</h3>
            <div className='free-upgrade-card subscription-item'>
              <div className='subscription-header'>
                <h4>Camera Free</h4>
                <div className='resolution'>
                  <div className='res'>320p</div>
                  <div className='label'>SD</div>
                </div>
              </div>
              <div className='subscription-details'>
                <h4 className='upgrade-heading'>Upgrade and improve your:</h4>
                <ul className='upgrade-features'>
                  {upgradeFeatures.map((feature, idx) => (
                    <li key={idx}>
                      <span className='checkmark'></span> {feature}
                    </li>
                  ))}
                </ul>
                <p>
                  <a href='https://ambientweather.com/enhanced-weather-camera-subscription' target='_blank'>Learn More</a>
                </p>
                <div className='subscription-action'>
                  <Link
                    to={`/subscriptions/subscribe?type=camera&deviceId=${macAddress}`}
                    className='upgrade-button'
                  >
                    Upgrade
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {showConnectCameraPromo && (
          <div className='free-camera-promo'>
            <div className='add-camera-box'>
              <Link to={`/devices#camera-${macAddress}`} className='add-camera-header'>
                <span className='plus-icon'>+</span>
                Add weather camera
              </Link>
              <div className='camera-links'>
                <a href='https://ambientweather.com/ambient-weather-network-solar-wi-fi-weather-camera' target='_blank' rel='noopener noreferrer'>
                  Solar wi-fi camera
                </a>
                <a href='https://ambientweather.com/wifi-weather-camera-wc-fmwac2k' target='_blank' rel='noopener noreferrer'>
                  Wi-fi camera
                </a>
              </div>
            </div>
            <h4 className='promo-heading'>Connect your weather cam and get:</h4>
            <ul className='features-list'>
              {cameraFeatures.map((feature, idx) => (
                <li key={idx}>
                  <span className='checkmark'></span> {feature}
                </li>
              ))}
            </ul>
            <h4 className='promo-heading'>Upgrade your experience with a subscription:</h4>
            <ul className='features-list'>
              {cameraUpgradeFeatures.map((feature, idx) => (
                <li key={idx}>
                  <span className='checkmark'></span> {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        {cameraSubscriptions.length > 0 && (
          <div className='subscription-group'>
            <h3 className='subscription-group-heading'>Camera Subscription</h3>
            <div className='subscriptions-list'>
              {cameraSubscriptions.map((sub, idx) => (
                <SubscriptionItem
                  key={idx}
                  sub={sub}
                  user={user}
                  device={device}
                  userActions={userActions}
                  index={subscriptions.indexOf(sub)}
                  macAddress={macAddress}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SubscriptionItem ({ sub, user, device, userActions, index, macAddress }) {
  const itemClasses = classNames('subscription-item', getSubscriptionTypeClasses(sub))

  // Determine if this is an annual subscription
  const isAnnual = sub.productName && sub.productName.includes('yearly')

  // Get resolution data for camera subscriptions
  const isCameraSub = isCameraSubscription(sub.productName)
  const isCellularSub = !isCameraSub
  let resolutionData = null
  const isSilverTier = sub.productName && sub.productName.includes('tier1')
  const isGoldTier = sub.productName && sub.productName.includes('tier2')

  // Calculate monthly product name for annual subscriptions
  const monthlyProductName = isAnnual && sub.productName
    ? sub.productName.replace('yearly', 'monthly')
    : null

  // Calculate annual product name for monthly camera subscriptions
  const annualProductName = !isAnnual && isCameraSub && sub.productName
    ? sub.productName.replace('monthly', 'yearly')
    : null

  if (isCameraSub) {
    const resolutionFeature = CAM_FEATURES[0] // Time-lapse resolution
    if (isSilverTier) {
      resolutionData = resolutionFeature.silver // 720p, HD
    } else if (isGoldTier) {
      resolutionData = resolutionFeature.gold // 1080p, Full HD
    }
  }

  return (
    <div className={itemClasses}>
      <div className='subscription-header'>
        <h4>{getSubscriptionDisplayName(sub)}</h4>
        <div>
          {(isAnnual || isCellularSub) && <div className='annual'>Annual</div>}
          {resolutionData && (
            <div className='resolution'>
              <div className='res'>{resolutionData.res}</div>
              <div className='label'>{resolutionData.sub}</div>
            </div>
          )}
        </div>
      </div>

      <div className='subscription-details'>
        {isCellularSub && (
          <div className='detail-row subscription-name-row'>
            <h4>{getSubscriptionDisplayName(sub)}</h4>
          </div>
        )}
        {sub?.willDowngradeTo ? (
          <div className='downgrade-scheduled-message'>
            <div className='downgrade-scheduled-text'>
              Downgrade to {getSubscriptionConfig(sub.willDowngradeTo)?.displayName || getSubscriptionConfig(sub.willDowngradeTo)?.label || 'new plan'} scheduled
            </div>
            {/* <div className='stay-message'>
              Stay on {getSubscriptionConfig(sub.productName)?.displayName || getSubscriptionConfig(sub.productName)?.label || 'current plan'} and keep your top tier features
            </div> */}
          </div>
        ) : (
          <div className='detail-row auto-renew-row'>
            <AutoRenewToggle
              sub={sub}
              user={user}
              userActions={userActions}
            />
            Auto-renew
          </div>
        )}
        <div className='detail-row renews'>
          <span className='label'>
            {sub?.willDowngradeTo ? 'Downgrades on:' : (sub?.cancelAtPeriodEnd ? 'Expires:' : 'Renews:')}
          </span>
          <span className='value'>{moment(sub.currentPeriodEnd * 1000).format('MMM D, YYYY')}</span>
        </div>
        <div className='detail-row renewal-price'>
          <span className='label'>Renewal Price:</span>
          <span className='value'>{displayPriceForSubscription(sub)}</span>
        </div>
        {isCameraSub && macAddress && (
          <div className='subscription-action'>
            {sub?.willDowngradeTo ? (
              <StayOnPlanButton
                sub={sub}
                user={user}
                macAddress={macAddress}
              />
            ) : (
              <>
                {isSilverTier && (
                  <>
                    <Link
                      to={`/subscriptions/subscribe?type=camera&deviceId=${macAddress}`}
                      className='upgrade-button'
                    >
                      Upgrade
                    </Link>
                    {!isAnnual && annualProductName && (
                      <Link
                        to={`/subscriptions/checkout?subscription=${annualProductName}&deviceId=${macAddress}`}
                        className='switch-to-annual-link'
                      >
                        Switch to Annual
                      </Link>
                    )}
                  </>
                )}
                {isGoldTier && (
                  <>
                    <Link
                      to={`/subscriptions/subscribe?type=camera&deviceId=${macAddress}`}
                      className='change-plan-link'
                    >
                      Change Plan
                    </Link>
                    {!isAnnual && annualProductName && (
                      <Link
                        to={`/subscriptions/checkout?subscription=${annualProductName}&deviceId=${macAddress}`}
                        className='switch-to-annual-link'
                      >
                        Switch to Annual
                      </Link>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

    </div>
  )
}

DeviceSubscriptionCard.displayName = 'DeviceSubscriptionCard'
